import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BarChart3,
  Calendar,
  CheckCircle,
  Copy,
  Plus,
  Search,
  Settings,
  ShieldOff,
  Trash2,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import advisorClientService, { AdvisorClientListItem, CreateClientRequest } from "../../services/advisorClientService";
import userConfigurationService, { ConfigData, UserConfiguration } from "../../services/UserConfigurationService";
import userConfigurationSchemaService, {
  ConfigurationSettingType,
  SchemaPropertyDescriptor,
  UserConfigurationSchemaDescriptor,
  normalizeConfigurationSettingType,
} from "../../services/userConfigurationSchemaService";
import userContractExclusionsService, { UserContractExclusion } from "../../services/userContractExclusionsService";
import investorStrategyService, { StrategyEntry } from "../../services/investorStrategyService";
import AdminAccountSummary from "../../components/AdminAccountSummary";
import ContractExclusionsModal from "../../components/ContractExclusionsModal";
import SplitScreenLayout from "../../components/SplitScreenLayout";
import { formatPercentage } from "../../utils/numberUtils";

// --- Configuration helpers (same logic as admin UsersPage) ---

type ConfigurationValueState = "true" | "false" | "unset" | "value";

interface SidebarConfigurationSetting {
  name: string;
  type: ConfigurationSettingType;
}

const SIDEBAR_ROOT_CONFIGURATION_KEYS = ["isbeta", "iscustomer", "isretirement", "tradingenabled"];
const SIDEBAR_USER_SETTING_KEYS = ["user_tradingenabled"];

const isObjectRecord = (val: unknown): val is Record<string, unknown> =>
  typeof val === "object" && val !== null && !Array.isArray(val);

const formatConfigurationLabel = (name: string): string =>
  name.split("_").filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");

const toSidebarSetting = (property: SchemaPropertyDescriptor): SidebarConfigurationSetting => ({
  name: property.name,
  type: normalizeConfigurationSettingType(property.type),
});

const uniqueSidebarSettings = (settings: SidebarConfigurationSetting[]): SidebarConfigurationSetting[] => {
  const seen = new Set<string>();
  return settings.filter((setting) => {
    if (seen.has(setting.name)) return false;
    seen.add(setting.name);
    return true;
  });
};

const getSidebarRootConfigurationSettings = (
  descriptor: UserConfigurationSchemaDescriptor | null,
): SidebarConfigurationSetting[] =>
  uniqueSidebarSettings([
    ...SIDEBAR_ROOT_CONFIGURATION_KEYS.map((name) => ({ name, type: "boolean" as const })),
    ...(descriptor?.rootLevelProperties ?? []).map(toSidebarSetting),
  ]);

const getSidebarUserSettingSettings = (
  descriptor: UserConfigurationSchemaDescriptor | null,
): SidebarConfigurationSetting[] =>
  uniqueSidebarSettings([
    ...SIDEBAR_USER_SETTING_KEYS.map((name) => ({ name, type: "boolean" as const })),
    ...(descriptor?.userSettingsProperties ?? []).map(toSidebarSetting),
  ]);

const readConfigurationValueDisplay = (
  data: ConfigData | null | undefined,
  setting: SidebarConfigurationSetting,
  scope: "root" | "userSettings",
): { state: ConfigurationValueState; label: string } => {
  const rawValue =
    scope === "root"
      ? data?.[setting.name]
      : isObjectRecord(data?.userSettings)
        ? data.userSettings[setting.name]
        : undefined;
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return { state: "unset", label: "unset" };
  }
  if (setting.type !== "boolean") {
    return { state: "value", label: String(rawValue) };
  }
  if (typeof rawValue === "boolean") {
    return { state: rawValue ? "true" : "false", label: rawValue ? "true" : "false" };
  }
  const normalized = String(rawValue).trim().toLowerCase();
  if (normalized === "true") return { state: "true", label: "true" };
  if (normalized === "false") return { state: "false", label: "false" };
  return { state: "unset", label: "unset" };
};

const configurationValueBadgeClass = (state: ConfigurationValueState): string => {
  if (state === "true") return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
  if (state === "false") return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
  if (state === "value") return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
  return "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500";
};

const ConfigurationSettingsSummary: React.FC<{
  title: string;
  settings: SidebarConfigurationSetting[];
  data: ConfigData | null | undefined;
  scope: "root" | "userSettings";
  isDarkMode: boolean;
  isLoading: boolean;
}> = ({ title, settings, data, scope, isDarkMode, isLoading }) => {
  if (isLoading) return null;
  return (
    <div className="mt-2">
      <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
        {title}
      </span>
      <div className="space-y-1 mt-1">
        {settings.map((setting) => {
          const { state, label } = readConfigurationValueDisplay(data, setting, scope);
          const badgeClass = configurationValueBadgeClass(state);
          return (
            <div key={`${scope}:${setting.name}`} className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className={`text-xs font-mono truncate ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {setting.name}
                </div>
                <div className={`text-[10px] truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  {formatConfigurationLabel(setting.name)}
                  <span className="ml-1 uppercase">{setting.type}</span>
                </div>
              </div>
              <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider rounded-full uppercase ${badgeClass}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Helpers ---
const isNotFoundError = (err: any): boolean => (err.response?.status || err.status) === 404;

interface AddClientFormState {
  clientName: string;
  ibkrAccountId: string;
}

const AdvisorClientsPage: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const { advisorAccountId: advisorAccountIdParam } = useParams<{ advisorAccountId: string }>();
  const advisorAccountId = Number(advisorAccountIdParam);

  const [clients, setClients] = useState<AdvisorClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState<AddClientFormState>({ clientName: "", ibkrAccountId: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdvisorClientListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Sidebar state
  const [sidebarClient, setSidebarClient] = useState<AdvisorClientListItem | null>(null);
  const [copiedId, setCopiedId] = useState(false);
  const currentSidebarUserIdRef = useRef<string | null>(null);

  // Sidebar data
  const [sidebarUserConfig, setSidebarUserConfig] = useState<UserConfiguration | null>(null);
  const [sidebarConfigLoading, setSidebarConfigLoading] = useState(false);
  const [sidebarExclusions, setSidebarExclusions] = useState<UserContractExclusion[]>([]);
  const [sidebarExclusionsLoading, setSidebarExclusionsLoading] = useState(false);
  const [sidebarStrategies, setSidebarStrategies] = useState<StrategyEntry[]>([]);
  const [sidebarStrategiesLoading, setSidebarStrategiesLoading] = useState(false);
  const [sidebarStrategiesError, setSidebarStrategiesError] = useState(false);

  // Configuration schema (admin only)
  const [configurationSchema, setConfigurationSchema] = useState<UserConfigurationSchemaDescriptor | null>(null);
  const [configurationSchemaLoading, setConfigurationSchemaLoading] = useState(false);

  // Contract exclusions modal
  const [exclusionsModalOpen, setExclusionsModalOpen] = useState(false);

  const userRoles = user?.userRoles?.map((ur: { role: string }) => ur.role) || [];
  const hasAdvisorRole = userRoles.includes("ADVISOR");

  // Load config schema (admin only - no advisor endpoint exists)
  useEffect(() => {
    if (hasAdvisorRole) return;
    const loadSchema = async () => {
      setConfigurationSchemaLoading(true);
      try {
        const schema = await userConfigurationSchemaService.getActiveSchema();
        setConfigurationSchema(schema.descriptor);
      } catch {
        // Non-critical
      } finally {
        setConfigurationSchemaLoading(false);
      }
    };
    loadSchema();
  }, [hasAdvisorRole]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let clientList: AdvisorClientListItem[];
      if (hasAdvisorRole) {
        ({ clients: clientList } = await advisorClientService.listClients());
      } else {
        ({ clients: clientList } = await advisorClientService.listClientsAdmin(advisorAccountId));
      }
      setClients(clientList);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to load clients";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [hasAdvisorRole, advisorAccountId]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Sidebar data fetching
  const fetchSidebarData = useCallback(async (userId: string) => {
    currentSidebarUserIdRef.current = userId;
    const isStillCurrent = () => currentSidebarUserIdRef.current === userId;

    setSidebarConfigLoading(true);
    setSidebarExclusionsLoading(true);
    setSidebarStrategiesLoading(true);
    setSidebarStrategiesError(false);

    // User config (admin only)
    if (!hasAdvisorRole) {
      try {
        const config = await userConfigurationService.getUserConfiguration(userId);
        if (isStillCurrent()) setSidebarUserConfig(config);
      } catch (err: any) {
        if (isStillCurrent()) {
          setSidebarUserConfig(isNotFoundError(err) ? null : null);
          if (!isNotFoundError(err)) console.error("Failed to fetch user config:", err);
        }
      } finally {
        if (isStillCurrent()) setSidebarConfigLoading(false);
      }
    } else {
      setSidebarUserConfig(null);
      setSidebarConfigLoading(false);
    }

    if (!isStillCurrent()) return;

    // Contract exclusions
    try {
      if (hasAdvisorRole) {
        const exclusions = await advisorClientService.getClientContractExclusions(userId);
        if (isStillCurrent()) setSidebarExclusions(exclusions);
      } else {
        const data = await userContractExclusionsService.getExclusionsForUser(userId);
        if (isStillCurrent()) setSidebarExclusions(data.exclusions || []);
      }
    } catch (err: any) {
      if (isStillCurrent()) {
        const status = err.response?.status || err.status;
        if (status === 404 || status === 400) {
          setSidebarExclusions([]);
        } else {
          console.error("Failed to fetch exclusions:", err);
          setSidebarExclusions([]);
        }
      }
    } finally {
      if (isStillCurrent()) setSidebarExclusionsLoading(false);
    }

    if (!isStillCurrent()) return;

    // Strategy
    try {
      if (hasAdvisorRole) {
        const strategies = await advisorClientService.getClientStrategy(userId);
        if (isStillCurrent()) setSidebarStrategies(strategies);
      } else {
        const strategies = await investorStrategyService.getStrategiesForUser(userId);
        if (isStillCurrent()) setSidebarStrategies(strategies);
      }
    } catch (err) {
      if (isStillCurrent()) {
        console.error("Failed to fetch strategies:", err);
        setSidebarStrategiesError(true);
      }
    } finally {
      if (isStillCurrent()) setSidebarStrategiesLoading(false);
    }
  }, [hasAdvisorRole]);

  const prepareSidebarForSwitch = useCallback(() => {
    setSidebarUserConfig(null);
    setSidebarExclusions([]);
    setSidebarStrategies([]);
    setSidebarStrategiesError(false);
    setSidebarConfigLoading(true);
    setSidebarExclusionsLoading(true);
    setSidebarStrategiesLoading(true);
  }, []);

  const handleClientSelect = useCallback((client: AdvisorClientListItem) => {
    if (currentSidebarUserIdRef.current === client.userId) return;
    currentSidebarUserIdRef.current = client.userId;
    prepareSidebarForSwitch();
    setSidebarClient(client);
  }, [prepareSidebarForSwitch]);

  const handleCloseSidebar = useCallback(() => {
    currentSidebarUserIdRef.current = null;
    setSidebarClient(null);
    setSidebarUserConfig(null);
    setSidebarExclusions([]);
    setSidebarStrategies([]);
    setSidebarStrategiesError(false);
    setSidebarConfigLoading(false);
    setSidebarExclusionsLoading(false);
    setSidebarStrategiesLoading(false);
  }, []);

  useEffect(() => {
    if (sidebarClient) {
      fetchSidebarData(sidebarClient.userId);
    }
  }, [sidebarClient, fetchSidebarData]);

  const handleCopyUserId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.clientName.trim() || !addForm.ibkrAccountId.trim()) return;

    try {
      setAddLoading(true);
      setAddError(null);
      const payload: CreateClientRequest = {
        clientName: addForm.clientName.trim(),
        ibkrAccountId: addForm.ibkrAccountId.trim(),
      };
      if (hasAdvisorRole) {
        await advisorClientService.createClient(payload);
      } else {
        await advisorClientService.createClientAdmin(advisorAccountId, payload);
      }
      setShowAddModal(false);
      setAddForm({ clientName: "", ibkrAccountId: "" });
      await fetchClients();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to create client";
      setAddError(message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      if (hasAdvisorRole) {
        await advisorClientService.deleteClient(deleteTarget.userId);
      } else {
        await advisorClientService.deleteClientAdmin(advisorAccountId, deleteTarget.userId);
      }
      // If the deleted client was selected in sidebar, close it
      if (sidebarClient?.userId === deleteTarget.userId) {
        handleCloseSidebar();
      }
      setDeleteTarget(null);
      await fetchClients();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to delete client";
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (client.name?.toLowerCase().includes(query)) ||
      client.email.toLowerCase().includes(query) ||
      (client.ibkrAccountId?.toLowerCase().includes(query)) ||
      (client.groupName?.toLowerCase().includes(query))
    );
  });

  // Build advisor service functions adapter for ContractExclusionsModal
  const advisorExclusionServiceFunctions = hasAdvisorRole ? {
    getExclusions: async (uid: string) => ({
      exclusions: await advisorClientService.getClientContractExclusions(uid),
    }),
    createExclusion: async (params: { userId: string; conId: number; message?: string }) =>
      advisorClientService.createClientContractExclusion(params.userId, params.conId, params.message),
    deleteExclusion: async (uid: string, conId: number) =>
      advisorClientService.deleteClientContractExclusion(uid, conId),
    deleteAllExclusions: (uid: string) =>
      advisorClientService.deleteAllClientContractExclusions(uid),
  } : undefined;

  const mainContent = (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Clients</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage your advisory clients and their IBKR accounts.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Client
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
          />
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={fetchClients}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 dark:bg-gray-800 rounded-lg h-16" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredClients.length === 0 && (
        <div className="py-24 bg-white dark:bg-rp-surface rounded-2xl border border-gray-200 dark:border-rp-separator flex flex-col items-center justify-center">
          <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {searchQuery ? "No clients match your search." : "No clients yet. Add your first client to get started."}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Add Client
            </button>
          )}
        </div>
      )}

      {/* Client list */}
      {!loading && !error && filteredClients.length > 0 && (
        <div className="bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-rp-separator text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">IBKR Account</div>
            <div className="col-span-2">Group</div>
            <div className="col-span-2">Added</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Table rows */}
          {/* ph-mask: redacts client names, emails, and IBKR account IDs
              from PostHog session replays. */}
          <div className="ph-mask">
          {filteredClients.map((client) => (
            <div
              key={client.userId}
              onClick={() => handleClientSelect(client)}
              className={`grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors cursor-pointer ${
                sidebarClient?.userId === client.userId
                  ? "bg-rp-amber/5 dark:bg-rp-amber/10 border-l-2 border-l-rp-amber"
                  : ""
              }`}
            >
              <div className="col-span-3 flex flex-col justify-center min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {client.name || "Unnamed"}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {client.email}
                </span>
              </div>
              <div className="col-span-3 flex items-center">
                <span className="text-sm font-mono text-gray-900 dark:text-white">
                  {client.ibkrAccountId || "-"}
                </span>
              </div>
              <div className="col-span-2 flex items-center">
                {client.groupName ? (
                  <span className="min-w-0 truncate text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                    {client.groupName}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">Ungrouped</span>
                )}
              </div>
              <div className="col-span-2 flex items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(client.createdAtUtc).toLocaleDateString()}
                </span>
              </div>
              <div className="col-span-2 flex items-center justify-end">
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(client); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  title="Remove client"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { setShowAddModal(false); setAddError(null); }}
          />
          <div className="relative bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Add Client</h3>
              <button
                onClick={() => { setShowAddModal(false); setAddError(null); }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-separator transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleAddClient} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client Name
                </label>
                <input
                  type="text"
                  value={addForm.clientName}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, clientName: e.target.value }))}
                  placeholder="e.g. John Smith"
                  className="w-full px-3 py-2.5 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  IBKR Account ID
                </label>
                <input
                  type="text"
                  value={addForm.ibkrAccountId}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, ibkrAccountId: e.target.value }))}
                  placeholder="e.g. U12345678"
                  className="w-full px-3 py-2.5 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  The client must already have an IBKR account under your advisor master.
                </p>
              </div>

              {addError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400">{addError}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setAddError(null); }}
                  className="px-4 py-2.5 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLoading || !addForm.clientName.trim() || !addForm.ibkrAccountId.trim()}
                  className="px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addLoading ? "Adding..." : "Add Client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!deleteLoading) { setDeleteTarget(null); setDeleteError(null); } }}
          />
          <div className="relative bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Remove Client</h3>
              <button
                onClick={() => { if (!deleteLoading) { setDeleteTarget(null); setDeleteError(null); } }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-separator transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
              Are you sure you want to remove this client?
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {deleteTarget.name || "Unnamed"}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {deleteTarget.email}
              {deleteTarget.ibkrAccountId && (
                <span className="font-mono"> ({deleteTarget.ibkrAccountId})</span>
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              This will unlink their IBKR account from your advisor master.
            </p>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">{deleteError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={deleteLoading}
                onClick={() => { setDeleteTarget(null); setDeleteError(null); }}
                className="px-4 py-2.5 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={handleDeleteClient}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const sidebarContent = sidebarClient ? (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Client Details
        </h2>
        <button
          onClick={handleCloseSidebar}
          className={`p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          x
        </button>
      </div>

      {/* Scrollable Content */}
      {/* ph-mask: redacts names, emails, provider IDs, and any
          other user PII from PostHog session replays. Admins/advisors
          themselves still see real data; only the recording is
          masked. */}
      <div className="ph-mask flex-1 overflow-y-auto space-y-4 pr-1">
        {/* User Identity Card */}
        <div className={`rounded-lg p-4 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}>
          <div className="flex items-center space-x-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0 ${
              isDarkMode ? "bg-blue-900 text-blue-200" : "bg-blue-100 text-blue-700"
            }`}>
              {(sidebarClient.name || sidebarClient.email).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`text-lg font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                {sidebarClient.name || "Unnamed"}
              </h3>
              <p className={`text-sm truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {sidebarClient.email}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <code className={`text-xs font-mono ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  {sidebarClient.userId.substring(0, 12)}...
                </code>
                <button
                  onClick={() => handleCopyUserId(sidebarClient.userId)}
                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
                    isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"
                  }`}
                  title="Copy full user ID"
                >
                  {copiedId ? (
                    <CheckCircle className="w-3 h-3 text-green-500" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Group + Added date */}
          <div className="flex gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            {sidebarClient.groupName && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                {sidebarClient.groupName}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              <Calendar className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
              <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Added {new Date(sidebarClient.createdAtUtc).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* IBKR Configuration Widget */}
        <div className={`rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Settings className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
              <h4 className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                IBKR Configuration
              </h4>
            </div>
          </div>
          <div className="p-3">
            {hasAdvisorRole ? (
              // Advisor: read-only display of IBKR account ID from client list
              <div className="space-y-2">
                {sidebarClient.ibkrAccountId ? (
                  <div className="flex items-center justify-between">
                    <span className={`text-xs uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      Account ID
                    </span>
                    <code className={`text-sm font-mono ${isDarkMode ? "text-green-400" : "text-green-600"}`}>
                      {sidebarClient.ibkrAccountId}
                    </code>
                  </div>
                ) : (
                  <div className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    No IBKR account configured
                  </div>
                )}
              </div>
            ) : (
              // Admin: full config widget with schema badges
              <div>
                {sidebarConfigLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</span>
                  </div>
                ) : sidebarUserConfig ? (
                  <div className="space-y-2">
                    {sidebarUserConfig.ibkrAccountId ? (
                      <div className="flex items-center justify-between">
                        <span className={`text-xs uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          Account ID
                        </span>
                        <code className={`text-sm font-mono ${isDarkMode ? "text-green-400" : "text-green-600"}`}>
                          {sidebarUserConfig.ibkrAccountId}
                        </code>
                      </div>
                    ) : (
                      <div className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        No IBKR account configured
                      </div>
                    )}
                    <ConfigurationSettingsSummary
                      title="Admin Settings"
                      settings={getSidebarRootConfigurationSettings(configurationSchema)}
                      data={sidebarUserConfig.data}
                      scope="root"
                      isDarkMode={isDarkMode}
                      isLoading={configurationSchemaLoading}
                    />
                    <ConfigurationSettingsSummary
                      title="User Settings"
                      settings={getSidebarUserSettingSettings(configurationSchema)}
                      data={sidebarUserConfig.data}
                      scope="userSettings"
                      isDarkMode={isDarkMode}
                      isLoading={configurationSchemaLoading}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      No configuration saved
                    </div>
                    <ConfigurationSettingsSummary
                      title="Admin Settings"
                      settings={getSidebarRootConfigurationSettings(configurationSchema)}
                      data={null}
                      scope="root"
                      isDarkMode={isDarkMode}
                      isLoading={configurationSchemaLoading}
                    />
                    <ConfigurationSettingsSummary
                      title="User Settings"
                      settings={getSidebarUserSettingSettings(configurationSchema)}
                      data={null}
                      scope="userSettings"
                      isDarkMode={isDarkMode}
                      isLoading={configurationSchemaLoading}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Investor Strategy Widget */}
        <div className={`rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
              <h4 className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                Investor Strategy
              </h4>
            </div>
            {!hasAdvisorRole && (
              <a
                href={`/admin/users/${sidebarClient.userId}/strategy`}
                className={`text-xs px-2 py-1 rounded ${isDarkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50"}`}
              >
                View
              </a>
            )}
          </div>
          <div className="p-3">
            {sidebarStrategiesLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</span>
              </div>
            ) : sidebarStrategiesError ? (
              <div className="text-sm text-red-500">
                Failed to load strategy
              </div>
            ) : sidebarStrategies.length > 0 ? (
              <div className="space-y-1">
                <div className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                  {sidebarStrategies.length} product{sidebarStrategies.length !== 1 ? "s" : ""},{" "}
                  {formatPercentage(sidebarStrategies.reduce((sum, s) => sum + parseFloat(s.weight), 0))} allocated
                </div>
              </div>
            ) : (
              <div className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                No strategy configured
              </div>
            )}
          </div>
        </div>

        {/* Strategy Analyze Widget */}
        <div className={`rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <BarChart3 className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
              <h4 className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                Strategy Analyze
              </h4>
            </div>
            {!hasAdvisorRole && (
              <a
                href={`/admin/users/${sidebarClient.userId}/analyze`}
                aria-label="Open strategy analyzer"
                className={`text-xs px-2 py-1 rounded ${isDarkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50"}`}
              >
                Open
              </a>
            )}
          </div>
          <div className="p-3">
            {sidebarStrategiesLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</span>
              </div>
            ) : sidebarStrategiesError ? (
              <div className="text-sm text-red-500">
                Failed to load strategy
              </div>
            ) : sidebarStrategies.length > 0 ? (
              <div className="space-y-2">
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Like-for-like customer analyzer for risk, concentration, benchmark behavior, and product movement.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-700"
                  }`}>
                    {sidebarStrategies.length} product{sidebarStrategies.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            ) : (
              <div className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                No strategy configured
              </div>
            )}
          </div>
        </div>

        {/* Account Summary Widget */}
        <AdminAccountSummary
          key={sidebarClient.userId}
          userId={sidebarClient.userId}
          userName={sidebarClient.name || sidebarClient.email}
          advisorMode={hasAdvisorRole}
          fetchSummaryOverride={hasAdvisorRole ? advisorClientService.getClientAccountSummary : undefined}
        />

        {/* Contract Exclusions Widget */}
        <div className={`rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <ShieldOff className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
              <h4 className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                Contract Exclusions
              </h4>
              {!sidebarExclusionsLoading && sidebarExclusions.length > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  isDarkMode ? "bg-orange-900/30 text-orange-300" : "bg-orange-100 text-orange-700"
                }`}>
                  {sidebarExclusions.length}
                </span>
              )}
            </div>
            <button
              onClick={() => setExclusionsModalOpen(true)}
              className={`text-xs px-2 py-1 rounded ${
                isDarkMode
                  ? "text-blue-400 hover:bg-blue-900/30"
                  : "text-blue-600 hover:bg-blue-50"
              }`}
            >
              Manage
            </button>
          </div>
          <div className="p-3">
            {sidebarExclusionsLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</span>
              </div>
            ) : sidebarExclusions.length > 0 ? (
              <div className="space-y-1.5">
                {sidebarExclusions.slice(0, 3).map((excl) => (
                  <div
                    key={excl.id}
                    className={`flex items-center justify-between text-sm p-1.5 rounded ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <code className={`font-mono text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      {excl.conId}
                    </code>
                    {excl.message && (
                      <span className={`text-xs truncate max-w-[150px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {excl.message}
                      </span>
                    )}
                  </div>
                ))}
                {sidebarExclusions.length > 3 && (
                  <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    +{sidebarExclusions.length - 3} more exclusions
                  </div>
                )}
              </div>
            ) : (
              <div className={`text-sm italic ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                No contract exclusions
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <SplitScreenLayout
        mainContent={mainContent}
        sidebarContent={sidebarContent || <div />}
        isSidebarOpen={!!sidebarClient}
        sidebarPosition="right"
        sidebarWidth="50%"
        sidebarMaxWidth="60%"
        onCloseSidebar={handleCloseSidebar}
      />

      {/* Contract Exclusions Modal */}
      <ContractExclusionsModal
        isOpen={exclusionsModalOpen}
        onClose={() => {
          setExclusionsModalOpen(false);
          // Refresh sidebar exclusions after modal close
          if (sidebarClient) {
            fetchSidebarData(sidebarClient.userId);
          }
        }}
        userId={sidebarClient?.userId || null}
        userName={sidebarClient?.name || sidebarClient?.email}
        serviceFunctions={advisorExclusionServiceFunctions}
      />
    </>
  );
};

export default AdvisorClientsPage;
